import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin } from "lucide-react";
import { CONFERENCE, PARTNERS } from "@/data/content";
import Countdown from "@/components/Countdown";

export default function Hero() {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink">
      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        poster="https://images.unsplash.com/photo-1643780668909-580822430155?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70"
        data-testid="hero-video"
      >
        <source src="/hero.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
      <div className="absolute inset-0 bg-ink/30" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 lg:px-12 pt-32 pb-14">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-mono text-[11px] sm:text-sm uppercase tracking-[0.22em] text-crimson font-semibold mb-5 max-w-2xl"
        >
          An international conference by Department of Biotechnology, Kalasalingam Academy of Research and Education with Arizona State University (USA) & IAAM
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="font-heading text-6xl sm:text-8xl lg:text-9xl font-bold text-white tracking-tighter leading-[0.92]"
        >
          BioInnovate <span className="text-crimson">2026</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16 }}
          className="font-heading text-2xl sm:text-4xl text-white/90 font-medium mt-4 tracking-tight"
        >
          {CONFERENCE.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.24 }}
          className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 text-white/80 text-sm sm:text-base"
        >
          <span className="font-mono font-semibold text-white">{CONFERENCE.dates}</span>
          <span className="hidden sm:inline text-white/30">/</span>
          <span className="flex items-center gap-1.5"><MapPin size={15} className="text-crimson" /> KARE, Krishnankoil, Tamil Nadu, India</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-5 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
        >
          Three days where biotech research becomes discovery, innovation, and enterprise, alongside experts from India and around the world.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.38 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#register")}
            data-testid="hero-register-btn"
            className="bg-crimson text-white font-semibold px-7 py-4 flex items-center gap-2 hover:bg-white hover:text-ink transition-colors group"
          >
            Register Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => scrollTo("#ai-workshop")}
            data-testid="hero-explore-btn"
            className="border border-white/40 text-white font-semibold px-7 py-4 flex items-center gap-2 hover:bg-white/10 transition-colors"
          >
            Explore the Tracks <ArrowDown size={18} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-10 pt-8 border-t border-white/15 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
        >
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Conference begins in</p>
            <Countdown light />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {PARTNERS.map((p) => (
              <span
                key={p}
                className="font-mono text-[10px] sm:text-xs uppercase tracking-wider text-white/70 border border-white/25 px-3 py-1.5"
              >
                {p}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
